import {
  X,
  Trash2,
  MapPin,
  User,
  Calendar,
  DollarSign,
  Edit,
  AlertTriangle,
  TrendingDown,
  FileText,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { WasteLog } from '../../types';
import {
  WASTE_TYPE_LABELS, WASTE_REASON_LABELS, WASTE_REASON_COLORS 
} from '../../types';

interface WasteLogDetailModalProps {
  readonly wasteLog: WasteLog | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit: (wasteLog: WasteLog) => void;
  readonly onDelete: (wasteLog: WasteLog) => void;
  readonly onViewItem: (wasteLog: WasteLog) => void;
}

export function WasteLogDetailModal({
  wasteLog,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onViewItem,
}: WasteLogDetailModalProps) {
  if (!isOpen || !wasteLog) return null;

  const reasonColor = WASTE_REASON_COLORS[wasteLog.reason];
  const isPreConsumer = wasteLog.type === 'pre-consumer';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className={`rounded-lg p-3 ${isPreConsumer ? 'bg-amber-100' : 'bg-purple-100'}`}>
              <Trash2 className={`h-8 w-8 ${isPreConsumer ? 'text-amber-600' : 'text-purple-600'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{wasteLog.itemName}</h2>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${reasonColor}`}>
                  {WASTE_REASON_LABELS[wasteLog.reason]}
                </span>
                <span className="text-sm text-muted-foreground">
                  {WASTE_TYPE_LABELS[wasteLog.type]}
                </span>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Cost Impact */}
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-red-600" />
                <span className="text-sm font-medium text-red-700">Estimated Cost Impact</span>
              </div>
              <span className="text-2xl font-bold text-red-600">
                ${wasteLog.estimatedCost.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Quantity */}
          <div className="rounded-lg border p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              Quantity Wasted
            </h3>
            <p className="text-2xl font-semibold">
              {wasteLog.quantity} <span className="text-base text-muted-foreground">{wasteLog.unit}</span>
            </p>
          </div>

          {/* Details */}
          <div className="rounded-lg border p-4">
            <h3 className="font-medium mb-3">Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="text-muted-foreground">Venue</span>
                  <p className="font-medium">{wasteLog.venueName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="text-muted-foreground">Logged By</span>
                  <p className="font-medium">{wasteLog.loggedByName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="text-muted-foreground">Date & Time</span>
                  <p className="font-medium">{new Date(wasteLog.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {wasteLog.notes && (
            <div className="rounded-lg border p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Notes
              </h3>
              <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-3">
                {wasteLog.notes}
              </p>
            </div>
          )}

          {/* Waste Type Info */}
          <div className={`rounded-lg p-4 ${isPreConsumer ? 'bg-amber-50' : 'bg-purple-50'}`}>
            <div className="flex items-start gap-3">
              {isPreConsumer ? (
                <TrendingDown className="h-5 w-5 text-amber-600 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-purple-600 mt-0.5" />
              )}
              <div>
                <h4 className={`font-medium ${isPreConsumer ? 'text-amber-800' : 'text-purple-800'}`}>
                  {isPreConsumer ? 'Pre-Consumer Waste' : 'Post-Consumer Waste'}
                </h4>
                <p className={`text-sm mt-1 ${isPreConsumer ? 'text-amber-700' : 'text-purple-700'}`}>
                  {isPreConsumer
                    ? 'Waste generated during preparation, storage, or before serving to customers.'
                    : 'Waste from customer plates or unconsumed served food.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 border-t bg-background p-4">
          <div className="flex flex-wrap gap-2 justify-between">
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(wasteLog)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onViewItem(wasteLog)}>
                <Package className="mr-2 h-4 w-4" />
                View Item
              </Button>
              <Button onClick={() => onEdit(wasteLog)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
